import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Building2 } from "lucide-react";
import { supabase } from "@/lib/supabase";

export interface CRMAccountLite {
  id: string;
  account_id: string;
  account_name: string;
  default_cost_code: string | null;
  traveller_full_name: string | null;
  is_active: boolean | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery?: string;
  onPick: (account: CRMAccountLite) => void;
}

const CRMAccountSearchDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  initialQuery = "",
  onPick,
}) => {
  const [accounts, setAccounts] = useState<CRMAccountLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(initialQuery);

  useEffect(() => {
    if (!open) return;
    setQ(initialQuery || "");
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("crm_accounts")
        .select(
          "id, account_id, account_name, default_cost_code, traveller_full_name, is_active"
        )
        .order("account_name", { ascending: true })
        .limit(2000);
      if (cancelled) return;
      if (error) {
        console.error("CRM account search load failed:", error);
        setAccounts([]);
      } else {
        setAccounts((data || []) as CRMAccountLite[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return accounts.slice(0, 200);
    return accounts
      .filter((a) => {
        const cc = (a.default_cost_code || "").toLowerCase();
        return (
          (a.account_id || "").toLowerCase().includes(s) ||
          (a.account_name || "").toLowerCase().includes(s) ||
          (a.traveller_full_name || "").toLowerCase().includes(s) ||
          cc.includes(s) ||
          cc.replace(/\D+/g, "").includes(s.replace(/\D+/g, ""))
        );
      })
      .slice(0, 200);
  }, [accounts, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Search CRM Account
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search account #, name, traveller, TM client #…"
              className="pl-8"
            />
          </div>

          <div className="border rounded-md max-h-[55vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading accounts…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                No matching accounts.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="text-left p-2">Account #</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Traveller</th>
                    <th className="text-left p-2">TM CC</th>
                    <th className="text-left p-2">Status</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr
                      key={a.id}
                      className="border-t border-border hover:bg-muted/30"
                    >
                      <td className="p-2 font-mono">{a.account_id}</td>
                      <td className="p-2">{a.account_name}</td>
                      <td className="p-2">{a.traveller_full_name || "—"}</td>
                      <td className="p-2 font-mono">
                        {a.default_cost_code || "—"}
                      </td>
                      <td className="p-2">
                        <Badge
                          className={
                            a.is_active
                              ? "bg-green-700"
                              : "bg-gray-600"
                          }
                        >
                          {a.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => {
                            onPick(a);
                            onOpenChange(false);
                          }}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CRMAccountSearchDialog;
